"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import ButtonWithLoader from "@/components/utility/button-with-loader";
import { capturePixelEvent } from "@/lib/pixel";
import { emailSchema } from "@/types/zod-types";
import { cn } from "@/utils/helpers";
import { validateData } from "@/utils/validators";
import { useSignIn, useSignUp } from "@clerk/nextjs";
import type { ClerkAPIError, EmailCodeFactor, SignInFirstFactor } from "@clerk/types";
import { ArrowRightCircle } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
type VerificationFlow = "sign-in" | "sign-up";
type ClerkErrorLike = {
  errors?: ClerkAPIError[];
};
function getClerkErrorMessage(error: unknown) {
  const clerkErrors = (error as ClerkErrorLike)?.errors;
  if (clerkErrors?.length) {
    return clerkErrors[0]?.longMessage || clerkErrors[0]?.message || "Something went wrong, please try again later.";
  }
  return error instanceof Error ? error.message : "Something went wrong, please try again later.";
}
function shouldAutoCreateAccount(error: unknown) {
  const clerkErrors = (error as ClerkErrorLike)?.errors ?? [];
  return clerkErrors.some(item => {
    const combinedMessage = `${item.code} ${item.message} ${item.longMessage ?? ""}`.toLowerCase();
    return combinedMessage.includes("identifier") && (combinedMessage.includes("not found") || combinedMessage.includes("couldn't find") || combinedMessage.includes("cannot find") || combinedMessage.includes("no account"));
  });
}
function getEmailCodeFactor(supportedFirstFactors?: SignInFirstFactor[]) {
  return supportedFirstFactors?.find((factor): factor is EmailCodeFactor => factor.strategy === "email_code");
}
export default function EmailOTPAuthFlow() {
  const {
    isLoaded: isSignInLoaded,
    signIn,
    setActive: setSignInActive
  } = useSignIn();
  const {
    isLoaded: isSignUpLoaded,
    signUp,
    setActive: setSignUpActive
  } = useSignUp();
  const [emailInput, setEmailInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [verificationFlow, setVerificationFlow] = useState<VerificationFlow | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get("redirect_url") || "/";
  const verifying = verificationFlow !== null;
  async function startSignInFlow(signInResource: NonNullable<typeof signIn>) {
    const {
      supportedFirstFactors
    } = await signInResource.create({
      identifier: emailInput
    });
    const emailCodeFactor = getEmailCodeFactor(supportedFirstFactors ?? undefined);
    if (!emailCodeFactor) {
      throw new Error("Email OTP is not available for this account.");
    }
    await signInResource.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: emailCodeFactor.emailAddressId
    });
    setVerificationFlow("sign-in");
  }
  async function startSignUpFlow(signUpResource: NonNullable<typeof signUp>) {
    await signUpResource.create({
      emailAddress: emailInput,
      firstName: emailInput.split("@")[0]
    });
    await signUpResource.prepareEmailAddressVerification();
    setVerificationFlow("sign-up");
  }
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isSignInLoaded || !isSignUpLoaded || !signIn || !signUp) {
      return;
    }
    setIsSubmitting(true);
    try {
      validateData(emailSchema, emailInput);
      try {
        await startSignInFlow(signIn);
      } catch (error_0) {
        if (!shouldAutoCreateAccount(error_0)) {
          throw error_0;
        }
        await startSignUpFlow(signUp);
      }
    } catch (error) {
      toast.error(getClerkErrorMessage(error));
      console.error("Error:", JSON.stringify(error, null, 2));
    } finally {
      setIsSubmitting(false);
    }
  }
  async function handleVerification(e_0: React.FormEvent) {
    e_0.preventDefault();
    if (!verificationFlow || !signIn || !signUp) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (verificationFlow === "sign-in") {
        const signInAttempt = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code: codeInput
        });
        if (signInAttempt.status !== "complete") {
          throw new Error("Something went wrong, please try again later.");
        }
        await setSignInActive({
          session: signInAttempt.createdSessionId
        });
      }
      if (verificationFlow === "sign-up") {
        const signUpAttempt = await signUp.attemptEmailAddressVerification({
          code: codeInput
        });
        if (signUpAttempt.status !== "complete") {
          throw new Error("Something went wrong, please try again later.");
        }
        await setSignUpActive({
          session: signUpAttempt.createdSessionId
        });
        capturePixelEvent("CompleteRegistration", {
          method: "email-otp"
        });
      }
      router.push(redirectUrl);
    } catch (error_1) {
      toast.error(getClerkErrorMessage(error_1));
      console.error("Error:", JSON.stringify(error_1, null, 2));
    } finally {
      setIsSubmitting(false);
    }
  }
  if (verifying) {
    return <form onSubmit={handleVerification} className="space-y-1">
        <div className="flex flex-col gap-3">
          <InputOTP maxLength={6} value={codeInput} onChange={value => setCodeInput(value)}>
            <InputOTPGroup className="h-12 w-full">
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>

          <ButtonWithLoader type="submit" pending={isSubmitting}>
            Verify OTP
          </ButtonWithLoader>

          <p className="p-2 text-xs text-muted-foreground">
            We have sent you an email containing the OTP code, enter it above.
          </p>
        </div>
      </form>;
  }
  return <form onSubmit={handleEmailSubmit} className="space-y-3">
      <div className="relative">
        <Input id="email" name="email" type="email" placeholder="Enter your email" value={emailInput} onChange={e_1 => setEmailInput(e_1.target.value)} className="focus-visible:ring-0" />

        <Button type="submit" disabled={!emailInput} className="absolute right-0 top-0 h-full rounded-l-none border border-l-0 bg-transparent text-foreground">
          <ArrowRightCircle size={24} className="absolute" />
        </Button>
      </div>

      <ButtonWithLoader type="submit" pending={isSubmitting} className={cn("w-full", emailInput ? "cursor-pointer" : "cursor-not-allowed")}>
        Send OTP
      </ButtonWithLoader>

      <p className="p-2 text-xs text-muted-foreground">
        We&apos;ll sign you in if your account already exists, or create it automatically after OTP verification.
      </p>
    </form>;
}